// Source dictionary (English) — every other file in locales/ mirrors these
// keys; lookups fall back to this file for anything a translation misses.
// Strings take {name} placeholders, interpolated by t(). Plural entries end
// in _one/_few/_many/_other and resolve through tp() + pluralCategory().
const en = {
  // ===== shared =====
  'common.createPlan': 'Create plan',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.retry': 'Retry',
  'common.activate': 'Activate',
  'common.deactivate': 'Deactivate',
  'common.enabled': 'Enabled',
  'common.disabled': 'Disabled',
  'common.viewTerms': 'View terms',
  'common.untitledPlan': 'Untitled plan',
  'common.savedToast': 'Saved successfully',

  // ===== nav / chrome =====
  'nav.home': 'Home',
  'nav.plans': 'Plans',
  'nav.settings': 'Settings',

  // Deployment tripwire page (App.js) — shown before auth, no locale loaded.
  'mismatch.title': 'Deployment environment mismatch',
  'mismatch.body': 'This frontend build calls {apiBase}, but it is served from {hostname} (expected: {expected}).',
  'mismatch.contact': 'Please contact technical support — this is a deployment configuration error, not a problem with your account.',

  // Install gate (auth.jsx)
  'install.title': "Couldn't connect to Shopify",
  'install.body': "Check that the backend is running and its SHOPIFY_API_KEY matches the app's client_id in shopify.app.toml.",
  'install.retry': 'Retry',

  // Loading / crash chrome
  'chrome.loadingBrand': 'FlexCover is loading…',
  'error.title': 'Something went wrong',
  'error.reload': 'Reload',

  // Support impersonation banner (support staff only)
  'support.badge': 'Support view',
  'support.shopUnknown': '(unknown store)',
  'support.expired': 'Token expired — regenerate it in the support console',
  'support.validUntil': 'Token valid until {time}',
  'support.exit': 'Exit',

  // ===== Home =====
  'home.title': 'Welcome to FlexCover: Protection Plans 👋',
  'home.last30days': 'Last 30 days',
  'home.loadingChart': 'Loading chart',
  'home.loadingOverview': 'Loading overview',
  'home.statOrders': 'Orders with plans',
  'home.statRevenue': 'Total plan revenue',
  'home.gettingStarted': 'Getting started',
  'home.allSet': 'All set 🎉',
  'home.completed': '{done} of {total} completed',
  'home.step1.title': 'Create your first plan',
  'home.step1.desc': 'Set the price, coverage and cart appearance for your protection offer.',
  'home.step2.title': 'Enable the app embed',
  'home.step2.desc': 'Turn on the FlexCover: Protection Plans block so the widget appears in your cart.',
  'home.step2.enable': 'Enable app embed',
  'home.step2.check': 'Check status',
  'home.step3.title': 'Get your first protected order',
  'home.step3.desc': 'With the widget live, customers add a plan in the cart — totals show in the cards above.',
  'home.appEmbed': 'App embed',
  'home.checkingEmbed': 'Checking app embed status',
  'home.embedEnabledBody': 'The protection widget is live on your storefront.',
  'home.embedTheme': '(theme: {theme})',
  'home.embedDisabledBody': 'The app is currently not active on your storefront.',
  'home.embedUnknown': 'Unknown',
  'home.embedUnknownBody': 'Storefront status is available once the app is connected to a store.',
  'home.embedUnknownReason': 'Could not read your live theme right now.',
  'home.enableEmbedHint': 'In Shopify admin, go to Online Store → Themes → Customize → App embeds, then toggle on FlexCover: Protection Plans.',
  'home.learnMorePrefix': 'Learn more about ',
  'home.learnMoreLink': 'how to use Protection Plans',

  // ===== Plans list (Dashboard) =====
  'plans.title': 'Plans',
  'plans.loading': 'Loading plans',
  'plans.errorTitle': 'Could not load plans',
  'plans.empty.heading': 'Create your first protection plan',
  'plans.empty.body': 'Offer shipping protection, extended warranties, return insurance and custom coverage — all priced your way.',
  'plans.loadDemo': 'Load demo plans',
  'plans.demoLoaded': 'Demo plans loaded',
  'plans.demoFailed': 'Failed to load demo plans: {message}',
  'plans.filterPlaceholder': 'Filter plans',
  'plans.addFilter': 'Add filter',
  'plans.statusHeading': 'Status',
  'plans.typeHeading': 'Protection type',
  'plans.statusActive': 'Active',
  'plans.statusInactive': 'Inactive',
  'plans.showing_one': 'Showing {count} plan',
  'plans.showing_other': 'Showing {count} plans',
  'plans.sortBy': 'Sort by',
  'plans.selectAll': 'Select all plans',
  'plans.selectedCount': '{count} selected',
  'plans.bulkDeleted_one': '{count} plan deleted',
  'plans.bulkDeleted_other': '{count} plans deleted',
  'plans.bulkActivated_one': '{count} plan activated',
  'plans.bulkActivated_other': '{count} plans activated',
  'plans.bulkDeactivated_one': '{count} plan deactivated',
  'plans.bulkDeactivated_other': '{count} plans deactivated',
  'plans.deleteBulkFailed': 'Failed to delete: {message}',
  'plans.updateBulkFailed': 'Failed to update: {message}',
  'plans.activated': 'Plan activated',
  'plans.deactivated': 'Plan deactivated',
  'plans.updateFailed': 'Failed to update plan: {message}',
  'plans.deleted': 'Plan deleted',
  'plans.deleteFailed': 'Failed to delete plan: {message}',
  'plans.actionsFor': 'Actions for {name}',
  'plans.activatePlan': 'Activate {name}',
  'plans.deactivatePlan': 'Deactivate {name}',
  'plans.onlineStore': 'Online store',
  'plans.optional': 'Optional',
  'plans.automatic': 'Automatic',
  'plans.noMatch': 'No plans match your search or filters.',
  'plans.deleteTitle': 'Delete protection plan?',
  // Rendered as: <strong>{name}</strong> <body> — name always first.
  'plans.deleteBody': 'will be removed from your store immediately. Customers will no longer see this protection option.',
  'plans.helpPrefix': 'Need help? ',
  'plans.helpLink': 'Drop us a line',
  'plans.helpSuffix': " or view the app's documentation and user tutorials.",

  // Sort options (Dashboard)
  'sort.date': 'Date (newest first)',
  'sort.name': 'Name (A–Z)',
  'sort.sortOrder': 'Cart order',
  'sort.price': 'Price (low to high)',

  // Plan type / price labels (constants.js values surfaced in the list)
  'planType.shipping_protection': 'Shipping Protection',
  'planType.warranty': 'Warranty',
  'planType.return_insurance': 'Return Insurance',
  'planType.custom': 'Custom Protection',
  'planTypeShort.shipping_protection': 'Shipping',
  'planTypeShort.warranty': 'Warranty',
  'planTypeShort.return_insurance': 'Return',
  'planTypeShort.custom': 'Custom',
  'priceType.fixed': 'Fixed Amount',
  'priceType.percentage': 'Percentage of Order',

  // ===== Plan editor page shell (PlanSettings) =====
  'plan.pageTitle': 'Protection plan',
  'plan.loading': 'Loading plan',
  'plan.notFound.heading': 'Plan not found',
  'plan.notFound.body': 'This protection plan may have been deleted.',
  'plan.backToPlans': 'Back to plans',
  'plan.newTitle': 'New protection plan',
  'plan.newSubtitle': 'Configure pricing and cart appearance, then create the plan',
  'plan.subtitle': 'Changes apply to your store after you save',
  'plan.statusTitle': 'Plan status',
  'plan.saveFailed': 'Failed to save plan: {message}',

  // ===== Plan editor form (ProtectionPlanForm) =====
  'form.section.type': 'Protection type',
  'form.section.basics': 'Plan basics',
  'form.section.pricing': '{type} pricing',
  'form.pricingFallback': 'Protection',
  'form.section.appearance': 'Appearance',
  'form.section.fulfillment': 'Fulfillment',

  'form.type.shipping_protection': 'Shipping protection',
  'form.type.shipping_protection.help': "Protect the customer's order against loss, damage or theft during shipping.",
  'form.type.warranty': 'Warranty',
  'form.type.warranty.help': 'Covers manufacturing defects and normal wear for a fixed period of time.',
  'form.type.return_insurance': 'Return insurance',
  'form.type.return_insurance.help': 'Lets customers return items within a window for a full refund.',
  'form.type.custom': 'Custom protection',
  'form.type.custom.help': 'Your own custom coverage message.',

  // Per-type seed copy (prefills the form; saved as plan data).
  'form.defaults.untitledPlan': 'Untitled plan',
  'form.defaults.viewTerms': 'View terms',
  'form.defaults.shipping_protection.displayName': 'Shipping protection',
  'form.defaults.shipping_protection.description': 'Protect your order from loss, damage and stolen.',
  'form.defaults.warranty.displayName': 'Extended warranty',
  'form.defaults.warranty.description': 'Covers manufacturing defects and normal wear for 2 years.',
  'form.defaults.return_insurance.displayName': 'Return insurance',
  'form.defaults.return_insurance.description': 'Return any item within 14 days for a full refund, no questions asked.',
  'form.defaults.custom.displayName': 'Protection plan',
  'form.defaults.custom.description': 'Add protection to your order.',

  // Language switcher (per-plan translations)
  'form.lang.select': 'Select language',
  'form.lang.default': '{name} (Default)',
  'form.translated': '(translated)',

  'form.field.planName.label': 'Plan name (internal)',
  'form.field.planName.help': 'For your reference only — customers never see this',
  'form.field.nameRequired': 'Internal name is required',
  'form.field.displayName.label': 'Display name',
  'form.field.displayNameRequired': 'Display name is required',
  'form.field.displayName.placeholder': 'Display name',
  'form.field.displayName.help': 'Shown to customers in the cart and at checkout',
  'form.field.displayName.helpPreset': 'Shown to customers browsing in {lang}',
  'form.field.displayName.helpPresetFallback': 'Shown to customers browsing in {lang} — empty fields fall back to {primary}',
  'form.field.description.label': 'Description',
  'form.field.description.placeholder': 'Description',
  'form.field.description.help': 'Short customer-facing description shown under the plan name',
  'form.field.disabledDescription.label': 'Disabled description',
  'form.field.disabledDescription.placeholder': 'Optional — shown while the plan is not selected',
  'form.field.disabledDescription.help': "Shown while the plan's checkbox is unticked (falls back to the description above when empty)",
  'form.field.footer.label': 'Footer',
  'form.field.footerText.label': 'Footer text',
  'form.field.footerLink.label': 'Footer link',
  'form.field.footerLink.help': 'Where the footer text links to',

  'form.price.type.label': 'Price type',
  'form.price.fixed': 'Amount ($)',
  'form.price.percentage': 'Percentage (%)',
  'form.price.advanced': 'Advanced setting',
  'form.price.pctSummary': '{pct}% of the order amount, priced from ${min} in steps of ${inc} up to ${max} — {count} variants on the plan product',
  'form.price.tiersSummary_one': 'The plan product gets 1 variant at {base} plus {count} interval variant for the cart value rules',
  'form.price.tiersSummary_other': 'The plan product gets 1 variant at {base} plus {count} interval variants for the cart value rules',

  'form.validation.priceRequired': 'Enter a value greater than 0',
  'form.validation.priceMax100': 'Percentage cannot exceed 100',
  'form.validation.warrantyYears': 'Enter a duration between 1 and 5 years',
  'form.validation.returnDays': 'Enter a window between 7 and 365 days',
  'form.validation.maxGreaterThanMin': 'Maximum must be greater than minimum',
  'form.validation.variantIdNumeric': 'Variant ID must be numeric (the ID of the hidden plan variant)',

  'form.icon.label': 'Icon',
  'form.icon.useEmoji': 'Use {emoji} as plan icon',
  'form.icon.removeCustom': 'Remove the custom icon and use the shield icon instead',
  'form.icon.upload': 'Upload a custom icon image',
  'form.icon.uploadButton': 'Upload',
  'form.icon.uploading': 'Uploading icon',
  'form.icon.uploadHelp': 'Upload your own icon — PNG, JPEG, GIF or WebP, up to 5 MB.',
  'form.icon.error.notImage': 'Please choose an image file (PNG, JPEG, GIF or WebP).',
  'form.icon.error.tooLarge': 'The image must be 5 MB or smaller.',

  'form.fulfillment.intro': 'Choose when the protection product is marked as fulfilled.',
  'form.fulfillment.manually': 'Manually fulfill',
  'form.fulfillment.first_item': 'Mark fulfilled when first item in the order is fulfilled',
  'form.fulfillment.all_items': 'Mark fulfilled when all other items in the order are fulfilled',
  'form.fulfillment.immediately': 'Mark fulfilled immediately after order creation',

  // Percentage pricing modal
  'form.adv.title': 'Advanced setting',
  'form.adv.minPrice': 'Minimum price',
  'form.adv.minPriceHelp': "The first variant's price — the smallest fee the plan charges",
  'form.adv.increment': 'Increment amount',
  'form.adv.incrementHelp': 'Price step between the generated variants',
  'form.adv.maxPriceLabel': 'Maximum price: ',
  'form.adv.explanation': 'The plan product is generated with {buckets} variants: the minimum price {min} takes one variant and the remaining {rest} variants step up by {inc}, up to a maximum price of {max}. At checkout the customer pays the first variant priced at or above {pct}% of the cart subtotal — a $100 cart at 2% targets $2.00, so the $3.01 variant applies. ',
  'form.learnMore': 'Learn more',

  // Cart-value tiers modal
  'form.tiers.title': 'Set price by cart value',
  'form.tiers.minCol': 'Min cart value',
  'form.tiers.maxCol': 'Max cart value',
  'form.tiers.priceCol': 'Protection price',
  'form.tiers.addTier': 'Add new tier',
  'form.tiers.deleteTier': 'Delete tier {index}',
  'form.tiers.explanation': 'Every rule needs a minimum, a maximum and a price (rules may overlap — the first matching rule applies). Each rule becomes its own variant on the plan product. If a cart total does not match any rule, the fixed price from the main setting applies. ',
  'form.tiers.error.minRequired': 'Minimum cart value is required',
  'form.tiers.error.maxRequired': 'Maximum cart value is required',
  'form.tiers.error.priceRequired': 'Protection price is required',
  'form.tiers.error.pricePositive': 'Price must be greater than 0',
  'form.tiers.error.minMax': 'Minimum must be less than or equal to maximum',

  // ===== Save bar =====
  'savebar.unsavedChanges': 'Unsaved changes',
  'savebar.discard': 'Discard',
  'savebar.saving': 'Saving',

  // ===== Preview panel (simulated cart) =====
  'preview.title': 'Preview',
  'preview.simulatedSubtotal': 'Simulated cart subtotal',
  'preview.cart': 'Cart',
  'preview.product': 'PRODUCT',
  'preview.total': 'TOTAL',
  'preview.subtotal': 'Subtotal',
  'preview.checkout': 'Checkout',
  'preview.noPlans': 'No active plans yet — customers will not see this widget.',
  'preview.autoAdded': 'Auto-added',
  'preview.unsaved': 'Unsaved',

  // ===== Settings page =====
  'settings.title': 'Settings',
  'settings.language.title': 'Language preference',
  'settings.language.help': "When you visit FlexCover, this is the language shown to you. Your store's language is not affected.",
  'settings.language.label': 'Language',
  'settings.language.updated': 'Language updated',
  'settings.language.saveFailed': 'Failed to save language preference: {message}'
};

export default en;
